import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import API_CONFIG from './config/api.config';
import { JWTAuthProvider } from './contexts/JWTAuthContext';
import { PublicConfigProvider, type PublicConfig } from './contexts/PublicConfigContext';
import { TenantProvider } from './contexts/TenantContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './index.css';
import { initPostHog } from './services/analytics/posthog';
import { initializeFaro, getWebInstrumentations } from '@grafana/faro-web-sdk';
import { registerSW } from 'virtual:pwa-register';
import { addResourceHints, unregisterAllServiceWorkers } from './utils/serviceWorker';
import { logger } from './utils/logger';
import { PostHogProvider } from 'posthog-js/react';
import posthog from './services/analytics/posthog';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

async function loadPublicConfig(): Promise<PublicConfig> {
  if (!API_CONFIG.apiUrl) {
    logger.warn('API URL not configured, skipping public config fetch');
    return {};
  }

  const url = `${API_CONFIG.apiUrl.replace(/\/+$/, '')}/config/public`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
    const data = await response.json();
    return {
      environment: data?.environment,
      posthog: data?.posthog,
      faro: data?.faro,
      maxUploadSizeMB: data?.maxUploadSizeMB,
      cupidUrl: data?.cupidUrl,
    };
  } catch (error) {
    logger.warn(`Failed to load public config from ${url}`, { error });
    return {};
  }
}

async function bootstrap() {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  // initPerformanceMonitoring();
  addResourceHints();
  if (import.meta.env.PROD) {
    registerSW({ immediate: true });
  } else {
    unregisterAllServiceWorkers();
  }

  const publicConfig = await loadPublicConfig();

  // Initialize Faro from runtime config (fetched from /config/public)
  if (publicConfig?.faro?.enabled && publicConfig.faro.collectorUrl && publicConfig.faro.apiKey) {
    try {
      initializeFaro({
        url: publicConfig.faro.collectorUrl,
        apiKey: publicConfig.faro.apiKey,
        app: {
          name: 'web-client',
          version: import.meta.env.VITE_APP_VERSION || '1.0.0',
        },
        instrumentations: [
          ...getWebInstrumentations({
            captureConsole: true,
          }),
        ],
      });
      logger.info('[Faro] Frontend observability initialized', {
        url: publicConfig.faro.collectorUrl,
      });
    } catch (error) {
      logger.warn('[Faro] Frontend observability setup error', { error });
    }
  } else {
    logger.info('[Faro] Frontend observability disabled');
  }

  // Initialize PostHog with runtime config (falls back to VITE_* in local dev)
  if (publicConfig?.posthog?.enabled && publicConfig.posthog.mode !== 'disabled') {
    initPostHog({
      host: publicConfig.posthog.host!,
      apiKey: publicConfig.posthog.apiKey!,
      proxyHost: publicConfig.posthog.proxyHost,
    });
  } else {
    initPostHog();
  }

  const root = createRoot(rootElement);

  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <PublicConfigProvider value={publicConfig}>
          <ThemeProvider>
            <JWTAuthProvider>
              <TenantProvider>
                <PostHogProvider client={posthog}>
                  <App />
                </PostHogProvider>
              </TenantProvider>
            </JWTAuthProvider>
          </ThemeProvider>
        </PublicConfigProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
